import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from "react-toastify";
import { getList } from "../common/BackendProvider.jsx";

const formatPrice = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue.toFixed(2) : "-";
};

const formatScore = (value) => {
    const numericValue = Number(value || 0);
    return `${numericValue > 0 ? "+" : ""}${numericValue.toFixed(0)}`;
};

const scoreTone = (value) => {
    const numericValue = Number(value || 0);
    if (numericValue > 70 || numericValue > 5) return "border-emerald-200 bg-emerald-50 text-emerald-700";
    if (numericValue > 55 || numericValue > 2) return "border-lime-200 bg-lime-50 text-lime-700";
    if (numericValue > 40 || numericValue > -1) return "border-amber-200 bg-amber-50 text-amber-700";
    if (numericValue > 25 || numericValue > -4) return "border-orange-200 bg-orange-50 text-orange-700";
    return "border-red-200 bg-red-50 text-red-700";
};

const getLogoUrl = (company) => {
    if (company?.logo_url) return company.logo_url;
    if (!company?.website) return null;
    const website = company.website.startsWith("http") ? company.website : `https://${company.website}`;
    try {
        return `https://www.google.com/s2/favicons?domain=${new URL(website).hostname}&sz=64`;
    } catch (e) {
        return null;
    }
};

const CompanyLogo = ({ company }) => {
    const [failed, setFailed] = useState(false);
    const logoUrl = getLogoUrl(company);

    return (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            {logoUrl && !failed ? (
                <img src={logoUrl} alt="" className="h-full w-full object-contain p-1" onError={() => setFailed(true)} />
            ) : (
                <span className="text-sm font-bold text-emerald-600">{(company?.symbol || "?").slice(0, 2)}</span>
            )}
        </div>
    );
};

const MetricTile = ({ label, value, detail }) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</div>
        <div className="mt-1 text-base font-bold text-slate-950">{value}</div>
        {detail ? <div className="mt-1 text-xs font-semibold text-slate-500">{detail}</div> : null}
    </div>
);

const WatchlistCard = ({ company }) => {
    const low = Number(company.low_24h || 0);
    const high = Number(company.high_24h || 0);
    const last = Number(company.last_price || 0);
    const range = high > low ? Math.max(0, Math.min(100, ((last - low) / (high - low)) * 100)) : 50;

    return (
        <Link
            to={`/company/${company.symbol}`}
            className="group block rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                    <CompanyLogo company={company} />
                    <div className="min-w-0">
                        <div className="truncate text-lg font-bold text-slate-950 group-hover:text-emerald-700">
                            {company.name}
                        </div>
                        <div className="mt-1 text-sm font-semibold text-slate-500">{company.symbol}</div>
                    </div>
                </div>
                <div className={`shrink-0 rounded-full border px-3 py-1 text-sm font-bold ${scoreTone(company.esg_score)}`}>
                    ESG {formatScore(company.esg_score)}
                </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <MetricTile label="Last Price" value={formatPrice(company.last_price)} detail="market" />
                <MetricTile label="24h High" value={formatPrice(company.high_24h)} detail="session" />
                <MetricTile label="24h Low" value={formatPrice(company.low_24h)} detail="session" />
            </div>

            <div className="mt-4">
                <div className="mb-2 flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
                    <span>Daily range position</span>
                    <span>{formatPrice(low)} - {formatPrice(high)}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${range}%` }} />
                </div>
            </div>
        </Link>
    );
};

const SummaryTile = ({ label, value, detail }) => (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
        <div className="mt-2 text-2xl font-bold text-slate-950">{value}</div>
        <div className="mt-1 text-sm font-semibold text-slate-500">{detail}</div>
    </div>
);

export function Watchlist() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        getList("watchlist", { pagination: { page: 1, perPage: 100 } }).then((response) => {
            setCompanies(response.data.companies || []);
            setLoading(false);
        }).catch(() => {
            toast.error("Error while loading companies", {});
            setLoading(false);
        });
    }, []);

    const stats = useMemo(() => {
        const priced = companies.filter((company) => Number(company.last_price) > 0).length;
        const averageEsg = companies.length
            ? companies.reduce((sum, company) => sum + Number(company.esg_score || 0), 0) / companies.length
            : 0;
        const totalPrice = companies.reduce((sum, company) => sum + Number(company.last_price || 0), 0);

        return { priced, averageEsg, totalPrice };
    }, [companies]);

    return (
        <div className="space-y-5 p-4 lg:p-8">
            <ToastContainer />
            <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-600">Tracked universe</div>
                <h1 className="mt-2 text-3xl font-bold text-slate-950">Watch List</h1>
                <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
                    Monitor companies you have marked for follow-up, with market price, ESG signal, and daily range context.
                </p>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
                <SummaryTile label="Companies" value={companies.length} detail="on watch list" />
                <SummaryTile label="Market Data" value={stats.priced} detail="with live prices" />
                <SummaryTile label="Avg ESG" value={formatScore(stats.averageEsg)} detail={`tracked value ${formatPrice(stats.totalPrice)}`} />
            </div>

            {loading ? (
                <div className="rounded-lg border border-slate-200 bg-white p-8 text-sm font-semibold text-slate-500 shadow-sm">
                    Loading watch list...
                </div>
            ) : (
                <div className="grid gap-4 xl:grid-cols-2">
                    {companies.map((company) => (
                        <WatchlistCard key={company.id || company.symbol} company={company} />
                    ))}
                </div>
            )}

            {!loading && companies.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm">
                    <div className="text-lg font-bold text-slate-950">No companies on your watch list yet</div>
                    <div className="mt-2 text-sm font-medium text-slate-500">
                        Open a company page and use the star control to add it here.
                    </div>
                </div>
            ) : null}
        </div>
    );
}
